import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { getV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFields } from 'src/client/spendesk-api';
import { useQuery as useLegacyQuery } from 'src/core/api/hooks/useQuery';
import { type QueryState } from 'src/core/api/queryState';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import {
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldMapping,
} from '../../accounting';

export const useAnalyticalFieldMappingsQuery = (
  isEnabled: boolean = true,
): QueryState<AnalyticalFieldMapping[]> => {
  const { company: companyId } = useParams();

  return useLegacyQuery<AnalyticalFieldMapping[], AnalyticalFieldMapping[]>({
    key: ['getAnalyticalFieldMappings', companyId],
    isEnabled,
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint:
        '/accounting-integration/chart-of-accounts/analytical-fields-mappings',
    },
    reshapeData(analyticalFieldMappings) {
      return analyticalFieldMappings;
    },
  });
};

export const useAnalyticalFieldMappingQueryV2 = (isEnabled: boolean = true) => {
  const companyId = useCompanyId();
  const { data, isLoading } = useQuery({
    queryKey: ['analytical-field-mapping', companyId],
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    queryFn: async () => {
      const response =
        await getV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFields({
          path: {
            companyId,
          },
        });

      if (response.statusCode === 200) {
        return response.body.map((field): AnalyticalFieldMappingV2 => field);
      }

      return [];
    },
  });

  return {
    isLoading,
    analyticalFieldMappings: data ?? [],
  };
};

export const useInvalidateAnalyticalFieldMappingQuery = () => {
  const queryClient = useQueryClient();
  const companyId = useCompanyId();
  return () => {
    queryClient.invalidateQueries({
      queryKey: ['analytical-field-mapping', companyId],
    });
  };
};
